import { useEffect, useState } from 'react';
import {
  ArrowLeftOutlined,
  CopyOutlined,
  DeleteOutlined,
  LeftOutlined,
  MoreOutlined,
  PlusOutlined,
  RightOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  App,
  Button,
  Dropdown,
  Card,
  Col,
  Divider,
  Flex,
  Form,
  Grid,
  Input,
  InputNumber,
  Popconfirm,
  Row,
  Select,
  Space,
  Switch,
  Tag,
  theme,
  Tooltip,
  Typography,
} from 'antd';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router';
import { AssuntoTreeSelect } from '@titularis/ui';
import { api } from '../../api';
import { copiarQuestao } from './copiarQuestao';
import { ComentarioResolucao } from '../../components/ComentarioResolucao';
import { EditorTexto } from '../../components/EditorTexto';
import { CitacoesConferidas } from '../../components/CitacoesConferidas';
import { DiferencaEntreVersoes } from '../../components/DiferencaEntreVersoes';
import { HistoricoVersoes } from '../../components/HistoricoVersoes';
import { SearchBoxCriavel } from '../../components/SearchBoxCriavel';
import { EnfileirarComentario } from '../../components/EnfileirarComentario';
import { useSession } from '../../auth';
import { useVizinhas, type Navegacao, type Vizinha } from './listaDeQuestoes';
import { anuladaNoAr, haVersaoParaPublicar } from './publicacaoDaQuestao';
import {
  ASSINATURA_VAZIA,
  assinaturaDoConteudo,
  conteudoParaSalvar,
  valoresDaVersao,
  type FormularioDeQuestao,
} from './conteudoDaQuestao';
import {
  ehDiscursiva,
  LETRAS,
  STATUS_QUESTAO_COR,
  rotuloDaSituacao,
  nomeDaQuestao,
  TIPO_DISCURSIVA_LABEL,
  TIPOS_DISCURSIVOS,
} from '../../dominio';

/** Quem pode publicar. O backend decide de verdade; a tela só reflete. */
const PAPEIS_QUE_PUBLICAM = ['publisher', 'owner'];

/**
 * Cadastro e edição de questão.
 *
 * Editar não publica: salvar cria uma versão nova e o aluno continua vendo a
 * versão vigente até alguém publicar. É por isso que a tela mostra as duas
 * coisas lado a lado - o que está sendo escrito e o que está no ar.
 *
 * **A chave remonta a tela a cada questão.** "Próxima" troca o `:id` sem trocar
 * de rota, e o React manteria o mesmo componente montado: o formulário seguiria
 * com o texto da questão anterior até a nova chegar da API, e o botão de salvar
 * - ligado pelo que já estava preenchido - gravaria o conteúdo de uma por cima
 * da outra. Trocar de questão é sempre uma tela nova.
 */
export function QuestaoFormPage() {
  const { id } = useParams<{ id: string }>();
  return <Questao key={id ?? 'nova'} />;
}

function Questao() {
  const { id } = useParams<{ id: string }>();
  const ehNova = !id || id === 'nova';
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();

  /*
   * De onde a pessoa veio, para o voltar devolver a lista como ela estava -
   * mesma página, mesmos filtros. Quem chegou por link direto não tem origem, e
   * aí volta para a lista limpa.
   */
  const origem = (location.state as { origem?: string } | null)?.origem;
  const voltarParaLista = origem ? `/questoes?${origem}` : '/questoes';

  /*
   * As vizinhas na lista de onde esta questão foi aberta - o que faz o "Próxima"
   * andar pelo conjunto filtrado, na ordem que estava na tela, sem passar pela
   * lista de novo. Questão nova não está em lista nenhuma.
   */
  const navegacao = useVizinhas(ehNova ? undefined : id, origem);
  const queryClient = useQueryClient();
  const { message, modal } = App.useApp();
  const { token } = theme.useToken();
  const telas = Grid.useBreakpoint();
  const compacto = !telas.md;
  const { data: sessao } = useSession();
  const [form] = Form.useForm<FormularioDeQuestao>();
  const [alterado, setAlterado] = useState(false);

  /**
   * Sair da questão, perguntando antes quando há alteração pendente.
   *
   * "Próxima" é um clique só, e leva embora o que ainda não virou versão. O
   * aviso vale para toda saída, inclusive o "Questões": uma porta que confirma
   * ao lado de outra que não ensina a não conferir nenhuma.
   */
  const sairPara = (destino: string, state?: { origem: string }) => {
    const ir = () => void navigate(destino, { state });
    if (!alterado) {
      ir();
      return;
    }

    modal.confirm({
      title: 'Sair sem salvar?',
      content: 'Há alterações que ainda não viraram versão. Sair agora as descarta.',
      okText: 'Sair sem salvar',
      okButtonProps: { danger: true },
      cancelText: 'Continuar editando',
      onOk: ir,
    });
  };

  const examId = Form.useWatch('examId', form) ?? searchParams.get('examId') ?? undefined;
  const disciplineIds = Form.useWatch('disciplineIds', form) ?? [];

  const disciplinas = useQuery({
    queryKey: ['disciplines', 'todas'],
    queryFn: () => api.listDisciplines(),
  });
  const nomeDaDisciplina = (id: string) => disciplinas.data?.find((d) => d.id === id)?.name;

  const questao = useQuery({
    queryKey: ['question', id],
    queryFn: () => api.getQuestion(id!),
    enabled: !ehNova,
  });

  const versoes = useQuery({
    queryKey: ['question-versions', id],
    queryFn: () => api.listQuestionVersions(id!),
    enabled: !ehNova,
  });

  /*
   * A régua do que conta como alteração: a assinatura do conteúdo da versão
   * carregada (ver `conteudoDaQuestao`).
   *
   * Ela sai da própria versão, e não de um retrato do formulário recém-semeado:
   * os controles reemitem onChange ao receberem o valor, e comparar dois
   * retratos brutos acusava alteração por diferença de forma - a chave em outra
   * ordem, `null` onde antes não havia chave, o `attrs` vazio que o editor
   * acrescenta. A pergunta certa é se o que seria salvo mudou, que é a mesma que
   * o backend responde ao recusar versão de conteúdo idêntico (G-DETERMINISM).
   *
   * Questão nova compara com o conteúdo vazio: escolher a prova não é conteúdo,
   * e cadastrar uma questão em branco a API recusaria.
   */
  const assinaturaSalva =
    questao.data?.latestVersion && !ehNova
      ? assinaturaDoConteudo(valoresDaVersao(questao.data, questao.data.latestVersion))
      : ASSINATURA_VAZIA;

  /*
   * Régua nova, contagem zerada: quando a versão carregada muda - ao abrir, ao
   * salvar, ao recarregar por trás -, nada foi alterado em cima dela ainda. É o
   * ajuste de estado durante a renderização: o React refaz esta renderização com
   * o valor novo, sem passar por um quadro dizendo que há o que salvar.
   */
  const [regua, setRegua] = useState(assinaturaSalva);
  if (regua !== assinaturaSalva) {
    setRegua(assinaturaSalva);
    setAlterado(false);
  }

  const recalcularAlterado = () =>
    setAlterado(
      assinaturaDoConteudo(form.getFieldsValue(true) as FormularioDeQuestao) !== assinaturaSalva,
    );

  const prova = useQuery({
    queryKey: ['exam', examId],
    queryFn: () => api.getExam(examId!),
    enabled: Boolean(examId),
  });

  /*
   * As vizinhas vão sendo carregadas antes de serem pedidas.
   *
   * A tela remonta a cada questão, e sem os dados em mãos o passo pela fila
   * começaria sempre por um formulário vazio esperando a resposta - piscando a
   * cada clique de quem está justamente clicando em sequência.
   */
  const anteriorId = navegacao.anterior?.id;
  const proximaId = navegacao.proxima?.id;
  useEffect(() => {
    for (const vizinho of [anteriorId, proximaId]) {
      if (!vizinho) continue;
      void queryClient.prefetchQuery({
        queryKey: ['question', vizinho],
        queryFn: () => api.getQuestion(vizinho),
      });
    }
  }, [anteriorId, proximaId, queryClient]);

  const discursiva = ehDiscursiva(prova.data?.module);
  const podePublicar = PAPEIS_QUE_PUBLICAM.includes(
    (sessao?.user as { role?: string } | undefined)?.role ?? 'user',
  );

  /*
   * A questão chega depois da primeira renderização: preenchemos a partir da
   * última versão salva, que é onde o trabalho parou.
   *
   * Alteração pendente manda: a questão é recarregada por trás a cada
   * invalidação - publicar, marcar amostra grátis, salvar noutra aba -, e
   * preencher de novo nessa hora traria o banco por cima do que ainda está sendo
   * escrito. O trabalho sumiria da tela sem nada dizer.
   */
  useEffect(() => {
    const versao = questao.data?.latestVersion;
    if (!questao.data || !versao || alterado) return;

    form.setFieldsValue(valoresDaVersao(questao.data, versao));
  }, [questao.data, alterado, form]);

  useEffect(() => {
    if (!ehNova) return;
    const examParam = searchParams.get('examId');
    if (examParam) form.setFieldValue('examId', examParam);
  }, [ehNova, searchParams, form]);

  const salvar = useMutation({
    mutationFn: async (valores: FormularioDeQuestao) => {
      const conteudo = conteudoParaSalvar(valores, discursiva);

      return ehNova
        ? api.createQuestion({ examId: valores.examId, ...conteudo } as never)
        : api.updateQuestion(id!, conteudo as never);
    },
    onSuccess: async (salva) => {
      message.success('Questão salva.');
      // Salvou: o que estava no formulário virou versão, sem alteração pendente.
      setAlterado(false);
      await queryClient.invalidateQueries({ queryKey: ['questions'] });
      if (ehNova) {
        /*
         * O detalhe vem antes da navegação: trocar o `nova` pelo id remonta a
         * tela, e sem os dados em mãos ela remontaria com os campos vazios logo
         * depois do "Questão salva." - o que parece que o trabalho sumiu.
         */
        await queryClient.prefetchQuery({
          queryKey: ['question', salva.id],
          queryFn: () => api.getQuestion(salva.id),
        });
        void navigate(`/questoes/${salva.id}`, { replace: true, state: location.state });
        return;
      }
      await queryClient.invalidateQueries({ queryKey: ['question', id] });
      await queryClient.invalidateQueries({ queryKey: ['question-versions', id] });
    },
    onError: (erro: Error) => message.error(erro.message),
  });

  const publicar = useMutation({
    mutationFn: () => api.publishQuestion(id!),
    onSuccess: async () => {
      message.success(
        questao.data?.status === 'annulled'
          ? 'Questão no ar, marcada como anulada. Os alunos a veem sem gabarito e sem resposta.'
          : 'Questão publicada. Os alunos já veem esta versão.',
      );
      await queryClient.invalidateQueries({ queryKey: ['question', id] });
      await queryClient.invalidateQueries({ queryKey: ['question-versions', id] });
      await queryClient.invalidateQueries({ queryKey: ['questions'] });
    },
    onError: (erro: Error) => message.error(erro.message),
  });

  const excluir = useMutation({
    mutationFn: () => api.deleteQuestion(id!),
    onSuccess: async () => {
      message.success('Questão excluída.');
      await queryClient.invalidateQueries({ queryKey: ['questions'] });
      void navigate(prova.data ? `/questoes?examId=${prova.data.id}` : '/questoes');
    },
    onError: (erro: Error) => message.error(erro.message),
  });

  const marcarGratuita = useMutation({
    mutationFn: (isFree: boolean) => api.setQuestionFree(id!, isFree),
    onSuccess: async (questaoAtualizada) => {
      message.success(
        questaoAtualizada.isFree
          ? 'Questão liberada como amostra grátis.'
          : 'Questão voltou a contar no limite semanal.',
      );
      await queryClient.invalidateQueries({ queryKey: ['question', id] });
      await queryClient.invalidateQueries({ queryKey: ['questions'] });
    },
    onError: (erro: Error) => message.error(erro.message),
  });

  const anular = useMutation({
    mutationFn: () => api.annulQuestion(id!),
    onSuccess: async () => {
      message.success('Questão anulada.');
      await queryClient.invalidateQueries({ queryKey: ['question', id] });
      await queryClient.invalidateQueries({ queryKey: ['questions'] });
    },
    onError: (erro: Error) => message.error(erro.message),
  });

  /*
   * A anulada sai do ar e continua anulada (D-61). É a mesma rota de "voltar
   * para rascunho", mas o destino é outro, e a mensagem diz o destino.
   */
  const tirarAnuladaDoAr = useMutation({
    mutationFn: () => api.unpublishQuestion(id!),
    onSuccess: async () => {
      message.success('Questão fora do ar. Ela continua anulada.');
      await queryClient.invalidateQueries({ queryKey: ['question', id] });
      await queryClient.invalidateQueries({ queryKey: ['questions'] });
    },
    onError: (erro: Error) => message.error(erro.message),
  });

  const desanular = useMutation({
    mutationFn: () => api.unannulQuestion(id!),
    onSuccess: async () => {
      message.success('Anulação desfeita. A questão voltou a ser rascunho.');
      await queryClient.invalidateQueries({ queryKey: ['question', id] });
      await queryClient.invalidateQueries({ queryKey: ['questions'] });
    },
    onError: (erro: Error) => message.error(erro.message),
  });

  // Só faz sentido avisar quando já houve publicação: numa questão que nunca
  // foi publicada não há "versão vigente" para o aviso contrastar.
  const temVersaoNaoPublicada =
    Boolean(questao.data?.currentVersionId) &&
    questao.data?.latestVersionId !== questao.data?.currentVersionId;

  // Há o que publicar quando a última versão salva ainda não é a vigente - seja
  // a primeira publicação (sem vigente ainda) ou uma edição depois de publicar.
  /*
   * "Voltar para rascunho" em vez de "despublicar": a tela já chama o estado de
   * "Rascunho", então a ação que nomeia o destino dispensa tradução mental - e
   * "despublicar" não é palavra dicionarizada.
   */
  const voltarParaRascunho = useMutation({
    mutationFn: () => api.unpublishQuestion(id!),
    onSuccess: async () => {
      message.success('Questão fora do ar. Ela voltou a ser rascunho.');
      await queryClient.invalidateQueries({ queryKey: ['question', id] });
      await queryClient.invalidateQueries({ queryKey: ['question-versions', id] });
      await queryClient.invalidateQueries({ queryKey: ['questions'] });
    },
    onError: (erro: Error) => message.error(erro.message),
  });

  const copiar = async (comComentarios: boolean) => {
    const versao = questao.data?.latestVersion;
    if (!versao) return;

    try {
      await copiarQuestao(versao, { comComentarios });
      message.success(
        comComentarios ? 'Questão e comentários copiados.' : 'Enunciado e alternativas copiados.',
      );
    } catch {
      /*
       * A área de transferência exige permissão e contexto seguro. Falhar
       * calado deixaria a pessoa colando o conteúdo antigo sem entender por
       * quê - e colar errado num material que vai para fora é pior que não
       * copiar.
       */
      message.error('O navegador não liberou a área de transferência.');
    }
  };

  const haQuePublicar = haVersaoParaPublicar(questao.data);
  const noArComoAnulada = anuladaNoAr(questao.data);

  const semPermissao = 'Seu nível de acesso não permite publicar questões.';

  /**
   * As ações secundárias da questão, definidas uma vez só.
   *
   * No desktop elas viram uma fileira de botões; no celular vão para o menu de
   * reticências, porque cinco botões não cabem em 390px e empilhá-los empurraria
   * o formulário para baixo da dobra.
   *
   * O texto de confirmação mora aqui, e não em cada desenho. São avisos sobre
   * apagar conteúdo e tirar questão do ar - duas cópias divergiriam no primeiro
   * ajuste, e a versão errada seria justamente a que alguém leu antes de
   * confirmar.
   */
  const acoesSecundarias = [
    /*
     * Anular e desanular dividem o mesmo lugar: são a mesma decisão nos dois
     * sentidos, e só uma delas faz sentido de cada vez. Antes, a anulada exibia
     * um "Anular" desabilitado - a tela dizia o que não dava para fazer sem
     * oferecer saída a quem tinha clicado por engano, e a única volta era
     * apagar a questão e cadastrá-la de novo.
     */
    questao.data?.status === 'annulled'
      ? {
          chave: 'desanular',
          rotulo: 'Desanular',
          titulo: 'Desfazer a anulação?',
          descricao: noArComoAnulada
            ? 'A questão sai do ar e volta para rascunho. Publicar depois é um clique.'
            : 'A questão volta para rascunho, e não direto para o ar. Publicar depois é um clique.',
          okText: 'Desanular',
          perigo: false,
          bloqueio: podePublicar ? null : semPermissao,
          desabilitada: !podePublicar,
          carregando: desanular.isPending,
          confirmar: () => desanular.mutate(),
        }
      : {
          chave: 'anular',
          rotulo: 'Anular',
          titulo: 'Anular a questão?',
          /*
           * Anular sempre tira do ar (D-61): o comentário foi escrito para uma
           * questão com gabarito, e mostrá-la marcada é o passo seguinte, depois
           * de revisá-lo.
           */
          descricao:
            'Ela sai do estudo dos alunos. Para mostrá-la marcada como anulada, publique depois de revisar o comentário. Nada é apagado, e dá para desfazer.',
          okText: 'Anular',
          perigo: false,
          bloqueio: podePublicar ? null : semPermissao,
          desabilitada: !podePublicar,
          carregando: anular.isPending,
          confirmar: () => anular.mutate(),
        },
    /*
     * A anulada no ar sai do ar e continua anulada: não vira rascunho, porque
     * anular é fato do concurso (D-61).
     */
    ...(noArComoAnulada
      ? [
          {
            chave: 'tirar-do-ar',
            rotulo: 'Tirar do ar',
            titulo: 'Tirar do ar esta questão anulada?',
            descricao:
              'Ela sai do estudo dos alunos e continua anulada. Publicar de novo é um clique.',
            okText: 'Tirar do ar',
            perigo: false,
            bloqueio: podePublicar ? null : semPermissao,
            desabilitada: !podePublicar,
            carregando: tirarAnuladaDoAr.isPending,
            confirmar: () => tirarAnuladaDoAr.mutate(),
          },
        ]
      : []),
    /*
     * Só aparece no que está no ar: um botão de tirar do ar numa questão que
     * nunca foi publicada seria enfeite desabilitado ocupando lugar.
     */
    ...(questao.data?.status === 'published'
      ? [
          {
            chave: 'rascunho',
            rotulo: 'Voltar para rascunho',
            titulo: 'Voltar esta questão para rascunho?',
            descricao:
              'Ela sai do ar para os alunos. A versão que estava publicada continua no histórico, e republicar depois é um clique.',
            okText: 'Voltar para rascunho',
            perigo: false,
            bloqueio: podePublicar ? null : semPermissao,
            desabilitada: !podePublicar,
            carregando: voltarParaRascunho.isPending,
            confirmar: () => voltarParaRascunho.mutate(),
          },
        ]
      : []),
    {
      chave: 'excluir',
      rotulo: 'Excluir',
      titulo: 'Excluir a questão?',
      descricao: 'Some a questão e todo o histórico de versões dela. Não tem volta.',
      okText: 'Excluir',
      perigo: true,
      bloqueio: podePublicar ? null : 'Seu nível de acesso não permite excluir questões.',
      desabilitada: !podePublicar,
      carregando: excluir.isPending,
      confirmar: () => excluir.mutate(),
    },
  ];

  const anulada = questao.data?.status === 'annulled';
  const motivoDoPublicar = !podePublicar
    ? semPermissao
    : !haQuePublicar
      ? noArComoAnulada
        ? 'A última versão já está no ar, marcada como anulada.'
        : 'Não há versão nova para publicar. Salve uma alteração primeiro.'
      : anulada
        ? 'Publica para os alunos, marcada como anulada: sem gabarito e sem resposta.'
        : 'Publica a última versão salva para os alunos.';

  const copiarMenu = {
    items: [
      { key: 'simples', label: 'Enunciado e alternativas' },
      { key: 'completa', label: 'Com comentários' },
    ],
    onClick: ({ key }: { key: string }) => void copiar(key === 'completa'),
  };

  return (
    <Space orientation="vertical" size="large" style={{ width: '100%' }}>
      {/*
        No celular o título e as ações empilham. Lado a lado em 390px, o
        `Space` original não envolvia e a fileira de botões empurrava a página
        para fora da tela - a tela inteira rolava de lado.
      */}
      <Flex
        align={compacto ? 'stretch' : 'start'}
        justify="space-between"
        gap={12}
        vertical={compacto}
        style={{ width: '100%' }}
      >
        <Flex vertical gap={0} style={{ minWidth: 0 }}>
          {/*
            A navegação toda numa fileira: de onde se veio e para onde se vai
            dentro da mesma lista. Andar pelas questões filtradas é navegação, e
            não uma ação sobre a questão - por isso fica aqui, e não junto de
            "Publicar" e "Excluir", do outro lado do cabeçalho.
          */}
          <Flex align="center" gap={4} wrap>
            <Button
              type="link"
              icon={<ArrowLeftOutlined />}
              style={{ padding: 0 }}
              onClick={() => sairPara(voltarParaLista)}
            >
              Questões
            </Button>
            {navegacao.temLista ? (
              <>
                <Divider orientation="vertical" style={{ marginInline: 4 }} />
                <PassoNaLista
                  navegacao={navegacao}
                  onIr={(vizinha) =>
                    sairPara(`/questoes/${vizinha.id}`, { origem: vizinha.origem })
                  }
                />
              </>
            ) : null}
          </Flex>
          {/* Envolve: "II. Peça prática" mais duas etiquetas não cabem numa
              linha de 390px, e sem envolver elas saíam pela direita. */}
          <Flex align="center" wrap gap={8} style={{ minWidth: 0 }}>
            <Typography.Title level={compacto ? 4 : 3} style={{ margin: 0 }}>
              {ehNova ? 'Cadastrar questão' : tituloDaQuestao(questao.data?.latestVersion)}
            </Typography.Title>
            {questao.data ? (
              <Tag color={STATUS_QUESTAO_COR[questao.data.status]} style={{ marginInlineEnd: 0 }}>
                {rotuloDaSituacao(questao.data.status, noArComoAnulada)}
              </Tag>
            ) : null}
            {questao.data?.isFree ? (
              <Tag color="blue" style={{ marginInlineEnd: 0 }}>
                Amostra grátis
              </Tag>
            ) : null}
          </Flex>
        </Flex>

        {!ehNova ? (
          /*
            Envolve no celular: mesmo reduzido a tres alvos, "Copiar" e
            "Publicar" lado a lado com o menu podem nao caber num aparelho
            estreito, e sem envolver eles sairiam pela direita.
          */
          <Flex gap={8} wrap align="center" justify={compacto ? 'flex-start' : 'flex-end'}>
            {/*
              Copiar para colar em outra plataforma. Vai em HTML e texto ao
              mesmo tempo: editor rico recebe com formatação, campo simples
              recebe legível.
            */}
            <Dropdown disabled={!questao.data?.latestVersion} menu={copiarMenu}>
              <Button icon={<CopyOutlined />}>Copiar</Button>
            </Dropdown>

            {/*
              Ao lado de "Copiar" porque é da mesma natureza: prepara trabalho
              sobre esta questão, sem mudar o que o aluno vê. Publicar continua
              sendo a única ação que muda o ar.
            */}
            {id ? (
              <EnfileirarComentario questionIds={[id]} size="middle" compacto={compacto} />
            ) : null}

            {/*
              No desktop, cada ação secundária é um botão. Cabe, e um clique é
              melhor que dois.
            */}
            {!compacto
              ? acoesSecundarias.map((acao) => (
                  <Tooltip key={acao.chave} title={acao.bloqueio ?? ''}>
                    {/* O span recebe o hover: botão desabilitado não dispara o
                        tooltip sozinho (não emite eventos de ponteiro). */}
                    <span style={{ display: 'inline-block' }}>
                      <Popconfirm
                        title={acao.titulo}
                        description={acao.descricao}
                        okText={acao.okText}
                        okButtonProps={{ danger: acao.perigo }}
                        cancelText="Cancelar"
                        disabled={acao.desabilitada}
                        onConfirm={acao.confirmar}
                      >
                        <Button
                          danger={acao.perigo}
                          icon={acao.chave === 'excluir' ? <DeleteOutlined /> : undefined}
                          loading={acao.carregando}
                          disabled={acao.desabilitada}
                          // O ícone do Antd entra no nome acessível ("delete
                          // Excluir"); o rótulo explícito é o que o leitor de
                          // tela anuncia.
                          aria-label={acao.rotulo}
                        >
                          {acao.rotulo}
                        </Button>
                      </Popconfirm>
                    </span>
                  </Tooltip>
                ))
              : null}

            <Tooltip title={motivoDoPublicar}>
              <span style={{ display: 'inline-block' }}>
                <Popconfirm
                  title={anulada ? 'Publicar esta questão anulada?' : 'Publicar esta versão?'}
                  description={
                    anulada
                      ? 'Os alunos passarão a vê-la marcada como anulada, com o comentário desta versão.'
                      : 'É esta versão que os alunos passarão a ver.'
                  }
                  okText="Publicar"
                  cancelText="Cancelar"
                  disabled={!podePublicar || !haQuePublicar}
                  onConfirm={() => publicar.mutate()}
                >
                  <Button
                    type="primary"
                    loading={publicar.isPending}
                    disabled={!podePublicar || !haQuePublicar}
                  >
                    Publicar
                  </Button>
                </Popconfirm>
              </span>
            </Tooltip>

            {/*
              No celular as secundárias viram menu.

              A confirmação aqui é `modal` e não `Popconfirm`: o menu fecha ao
              clicar, e um balão ancorado num item que acabou de sumir não tem
              onde se apoiar. O texto é o mesmo, porque vem do mesmo lugar.
            */}
            {compacto ? (
              <Dropdown
                trigger={['click']}
                menu={{
                  items: acoesSecundarias.map((acao) => ({
                    key: acao.chave,
                    // O nome da ação sempre aparece; o motivo do bloqueio entra
                    // como sufixo. Trocar um pelo outro deixaria o item dizendo
                    // por que não dá, sem dizer o que era.
                    label: acao.bloqueio ? `${acao.rotulo} (sem permissão)` : acao.rotulo,
                    danger: acao.perigo,
                    disabled: acao.desabilitada,
                  })),
                  onClick: ({ key }) => {
                    const acao = acoesSecundarias.find((item) => item.chave === key);
                    if (!acao) return;
                    modal.confirm({
                      title: acao.titulo,
                      content: acao.descricao,
                      okText: acao.okText,
                      okButtonProps: { danger: acao.perigo },
                      cancelText: 'Cancelar',
                      onOk: acao.confirmar,
                    });
                  },
                }}
              >
                <Button icon={<MoreOutlined />} aria-label="Mais ações" />
              </Dropdown>
            ) : null}
          </Flex>
        ) : null}
      </Flex>

      {questao.error ? (
        <Alert type="error" showIcon title={(questao.error as Error).message} />
      ) : null}

      {/*
        A regra é do backend, mas o aviso vem antes: descobrir que a prova
        precisa estar publicada só na hora de publicar a questão é descobrir
        tarde.
      */}
      {!ehNova && prova.data && prova.data.status !== 'published' ? (
        <Alert
          type="warning"
          showIcon
          title="A prova desta questão ainda não está publicada."
          description="Enquanto ela estiver em rascunho, publicar a questão não a torna visível para os alunos."
          action={
            <Button size="small" onClick={() => void navigate(`/provas/${prova.data!.id}`)}>
              Abrir a prova
            </Button>
          }
        />
      ) : null}

      {temVersaoNaoPublicada ? (
        <Alert
          type="info"
          showIcon
          title="Há uma versão salva depois da última publicação."
          description="Os alunos continuam vendo a versão vigente até você publicar."
        />
      ) : null}

      {/*
        Logo abaixo do aviso porque responde a pergunta que ele deixa em aberto:
        "mudou o quê?". Sem isto, saber a resposta exigia abrir a questao
        inteira e comparar de memoria com o que estava no ar.
      */}
      {temVersaoNaoPublicada && questao.data ? (
        <DiferencaEntreVersoes questao={questao.data} />
      ) : null}

      {/*
        Sugestão da pipeline: o conteúdo abaixo não passou por revisão humana.
        Dizer isso na tela é o que impede a sugestão de ser lida como se alguém
        já tivesse conferido (G-HUMAN-GATE).
      */}
      {questao.data?.latestVersion?.state === 'suggestion' ? (
        <Alert
          type="warning"
          showIcon
          /*
           * O módulo vem do histórico, e não fixo no texto. O rótulo era
           * "Cadastro Assistido" sempre, e virou mentira quando nasceu o
           * Comentário Assistido: a tela dizia que o comentário tinha sido
           * "transcrito", quando ele foi escrito.
           */
          title={
            versoes.data?.[0]?.ingestionKind === 'comentario'
              ? 'Esta versão é uma sugestão do Comentário Assistido.'
              : 'Esta versão é uma sugestão do Cadastro Assistido.'
          }
          description={
            versoes.data?.[0]?.ingestionKind === 'comentario'
              ? 'Foi escrita a partir do acervo e ninguém revisou ainda. Confira o conteúdo antes de publicar; salvar cria uma versão sua por cima.'
              : 'Foi transcrita automaticamente e ninguém revisou ainda. Confira o conteúdo antes de publicar; salvar cria uma versão sua por cima.'
          }
        />
      ) : null}

      <Form<FormularioDeQuestao>
        form={form}
        layout="vertical"
        // Compara com o retrato do estado carregado: se um controle reemitir o
        // mesmo valor ao ser semeado, o retrato bate e não conta como alteração.
        onValuesChange={recalcularAlterado}
        onFinish={(valores) => salvar.mutate(valores)}
      >
        <Row gutter={[16, 16]}>
          <Col xs={24} xl={16}>
            <Space orientation="vertical" size="middle" style={{ width: '100%' }}>
              <Card size="small" title="Classificação">
                <Row gutter={16}>
                  <Col xs={24} md={16}>
                    <Form.Item
                      name="examId"
                      label="Prova"
                      rules={[{ required: true, message: 'A questão precisa pertencer a uma prova.' }]}
                    >
                      <SearchBoxCriavel
                        disabled={!ehNova}
                        queryKey={['exams', 'busca']}
                        placeholder="Buscar prova"
                        rotulo="Prova"
                        rotuloAtual={prova.data?.name}
                        buscar={async (termo) => (await api.listExams({ termo })).items}
                        acaoExtra={
                          <Button
                            type="text"
                            block
                            icon={<PlusOutlined />}
                            style={{ textAlign: 'left' }}
                            onMouseDown={(evento) => evento.preventDefault()}
                            onClick={() => void navigate('/provas/nova')}
                          >
                            Cadastrar prova
                          </Button>
                        }
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={8}>
                    <Form.Item name="position" label="Ordem na prova">
                      <InputNumber style={{ width: '100%' }} min={1} placeholder="1" />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="disciplineIds"
                      label="Disciplinas"
                      tooltip="Uma questão pode pertencer a mais de uma disciplina."
                    >
                      <Select
                        mode="multiple"
                        allowClear
                        showSearch
                        optionFilterProp="label"
                        placeholder="Escolha as disciplinas"
                        loading={disciplinas.isPending}
                        options={(disciplinas.data ?? []).map((item) => ({
                          value: item.id,
                          label: item.name,
                        }))}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="assuntos"
                      label="Assuntos e delimitações"
                      tooltip="A delimitação aparece dentro do assunto a que pertence."
                    >
                      <AssuntoTreeSelect
                        disciplineIds={disciplineIds}
                        carregarArvore={(disciplinaId) => api.subjectTree(disciplinaId)}
                        nomeDaDisciplina={nomeDaDisciplina}
                        placeholder={
                          disciplineIds.length > 0
                            ? 'Escolha assuntos e delimitações'
                            : 'Escolha a disciplina primeiro'
                        }
                      />
                    </Form.Item>
                  </Col>
                </Row>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  Assuntos e delimitações são criados e ordenados na tela de{' '}
                  <Typography.Link onClick={() => void navigate('/cadastros')}>
                    Cadastros
                  </Typography.Link>
                  .
                </Typography.Text>
              </Card>

              {discursiva ? <QuestaoDiscursiva /> : <QuestaoObjetiva anulada={anulada} />}
            </Space>
          </Col>

          <Col xs={24} xl={8}>
            <Space orientation="vertical" size="middle" style={{ width: '100%' }}>
              {/*
                No celular quem salva é a barra fixa do rodapé, e este cartão
                sairia sobrando: dois botões de salvar na mesma tela é a pior
                forma de perguntar qual dos dois vale.
              */}
              {!compacto ? (
              <Card size="small">
                <Space orientation="vertical" size="small" style={{ width: '100%' }}>
                  <Tooltip
                    title={alterado ? '' : 'Faça uma alteração no formulário para salvar uma nova versão.'}
                  >
                    {/* O span recebe o hover: botão desabilitado não dispara o
                        tooltip sozinho (não emite eventos de ponteiro). */}
                    <span style={{ display: 'block' }}>
                      <Button
                        type="primary"
                        block
                        htmlType="submit"
                        loading={salvar.isPending}
                        disabled={!alterado}
                      >
                        {ehNova ? 'Cadastrar questão' : 'Salvar nova versão'}
                      </Button>
                    </span>
                  </Tooltip>
                  <Typography.Text type="secondary">
                    Salvar cria uma versão e não altera o que o aluno vê. Conteúdo idêntico ao da
                    última versão não gera versão nova.
                  </Typography.Text>
                </Space>
              </Card>
              ) : null}

              {!ehNova ? (
                <Card size="small" title="Amostra grátis">
                  <Space orientation="vertical" size={8} style={{ width: '100%' }}>
                    <Space align="center" size={10}>
                      <Switch
                        checked={questao.data?.isFree ?? false}
                        loading={marcarGratuita.isPending}
                        disabled={!podePublicar}
                        aria-label="Liberar como amostra grátis"
                        onChange={(valor) => marcarGratuita.mutate(valor)}
                      />
                      <Typography.Text>Liberar para todos</Typography.Text>
                    </Space>
                    <Typography.Text type="secondary">
                      A questão fica visível sem consumir o limite semanal do aluno gratuito. Serve
                      de amostra da qualidade da resolução.
                    </Typography.Text>
                  </Space>
                </Card>
              ) : null}

              {/*
                Antes do histórico porque é o que se lê para decidir agora: o
                histórico conta de onde a versão veio, este cartão diz se o que
                está escrito bate com a lei.
              */}
              {!ehNova && id ? (
                <Card size="small" title="Citações conferidas">
                  <CitacoesConferidas questionId={id} />
                </Card>
              ) : null}

              {!ehNova ? (
                <Card size="small" title="Histórico de versões">
                  <HistoricoVersoes
                    versoes={versoes.data ?? []}
                    versaoVigenteId={questao.data?.currentVersionId ?? null}
                    carregando={versoes.isPending}
                  />
                </Card>
              ) : null}
            </Space>
          </Col>
        </Row>

        {/*
          A barra de salvar, fixa no rodapé do celular.

          Sem ela, o botão ficava na coluna lateral - que no celular vem
          **depois** do formulário inteiro: enunciado, cinco itens e três abas
          de comentário. Salvar exigia rolar a tela toda para baixo, e depois
          voltar para continuar escrevendo.

          Fica dentro do `Form` de propósito: assim o botão segue sendo `submit`
          de verdade, e não um `onClick` que chama o formulário de fora.
        */}
        {compacto ? (
          <div
            style={{
              position: 'sticky',
              bottom: 0,
              zIndex: 5,
              marginTop: 16,
              padding: 12,
              background: token.colorBgElevated,
              border: `1px solid ${token.colorSplit}`,
              borderRadius: token.borderRadius,
              boxShadow: token.boxShadowSecondary,
            }}
          >
            <Button
              type="primary"
              block
              htmlType="submit"
              loading={salvar.isPending}
              disabled={!alterado}
            >
              {ehNova ? 'Cadastrar questão' : 'Salvar nova versão'}
            </Button>
            <Typography.Text
              type="secondary"
              style={{ fontSize: 12, display: 'block', marginTop: 6, textAlign: 'center' }}
            >
              {alterado
                ? 'Salvar cria uma versão; o aluno só vê ao publicar.'
                : 'Nada alterado desde a última versão.'}
            </Typography.Text>
          </div>
        ) : null}
      </Form>
    </Space>
  );
}

/**
 * Anterior e Próxima dentro da lista de onde a questão foi aberta.
 *
 * **Com a posição no meio dos dois.** "12 de 137" é o que diz que ainda há
 * caminho pela frente e quanto falta; sem ela, os dois botões respondem "existe
 * vizinha?" e nada mais - e conferir uma prova inteira vira um trajeto sem
 * régua.
 *
 * As pontas ficam desabilitadas, e não escondidas: botão que some muda a posição
 * dos outros justamente quando se está clicando em sequência.
 */
function PassoNaLista({
  navegacao,
  onIr,
}: {
  navegacao: Navegacao;
  onIr: (vizinha: Vizinha) => void;
}) {
  const { anterior, proxima, posicao, total, carregando } = navegacao;

  const passo = (vizinha: Vizinha | null, rotulo: string, semVizinha: string, fim: boolean) => (
    // Nada de tooltip enquanto a lista carrega: naquele instante o botão está
    // desligado por falta de resposta, e não por ser a ponta da lista.
    <Tooltip title={vizinha || carregando ? '' : semVizinha}>
      {/* O span recebe o hover: botão desabilitado não dispara o tooltip
          sozinho (não emite eventos de ponteiro). */}
      <span style={{ display: 'inline-block' }}>
        <Button
          size="small"
          type="text"
          disabled={!vizinha}
          iconPosition={fim ? 'end' : 'start'}
          icon={fim ? <RightOutlined /> : <LeftOutlined />}
          onClick={() => vizinha && onIr(vizinha)}
        >
          {rotulo}
        </Button>
      </span>
    </Tooltip>
  );

  return (
    <Flex align="center" gap={2} wrap>
      {passo(anterior, 'Anterior', 'Esta é a primeira questão da lista.', false)}
      {posicao ? (
        <Typography.Text type="secondary" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
          {posicao} de {total}
        </Typography.Text>
      ) : null}
      {passo(proxima, 'Próxima', 'Esta é a última questão da lista.', true)}
    </Flex>
  );
}

/**
 * Enunciado, itens a-e, gabarito e resolução.
 *
 * Na anulada (D-61) o editor fala como o aluno vai ler: não há item certo nem
 * errado, e a letra que ficar guardada é a do gabarito preliminar, que nunca
 * sai para o aluno. Dizer "está certa" aqui faria o comentário ser escrito para
 * defender uma letra que a banca derrubou.
 */
function QuestaoObjetiva({ anulada = false }: { anulada?: boolean }) {
  const compacto = !Grid.useBreakpoint().md;
  const form = Form.useFormInstance<FormularioDeQuestao>();
  const itens = Form.useWatch(['options'], form);
  const quantosItens = Array.isArray(itens) ? itens.length : 0;
  /*
   * O gabarito, para o editor mostrar a mesma hierarquia que o aluno verá.
   *
   * Quem escreve o comentário do item precisa saber, sem procurar, se está
   * explicando o acerto ou o erro - é o que muda o texto inteiro.
   */
  const gabarito = Form.useWatch('correctOption', form);

  return (
    <>
      <Card size="small" title="Enunciado">
        <Form.Item name="statement" noStyle>
          <EditorTexto minHeight={140} placeholder="Enunciado da questão" />
        </Form.Item>
      </Card>

      <Card size="small" title="Itens e gabarito">
        <Form.List name="options">
          {(campos, { add, remove }) => (
            <Space orientation="vertical" size="middle" style={{ width: '100%' }}>
              {campos.map((campo, indice) => (
                // Flex, e não Space: o Space embrulha cada filho e o editor não
                // cresceria, ficando numa coluna estreita no meio do cartão.
                <Flex key={campo.key} align="flex-start" gap={8} style={{ width: '100%' }}>
                  <Tag
                    color={!anulada && LETRAS[indice] === gabarito ? 'success' : undefined}
                    style={{ marginTop: 8 }}
                  >
                    {LETRAS[indice]})
                  </Tag>
                  <Space
                    orientation="vertical"
                    size={4}
                    style={{ flex: 1, minWidth: 0, width: '100%' }}
                  >
                    <Form.Item name={[campo.name, 'content']} noStyle>
                      <EditorTexto minHeight={70} placeholder={`Item ${LETRAS[indice]}`} />
                    </Form.Item>
                    {/*
                      O comentário do item, recuado e rotulado.

                      Eram dois editores idênticos empilhados, separados só pelo
                      texto de placeholder - que some no primeiro caractere
                      digitado. Depois disso não havia nada dizendo onde
                      terminava a alternativa e começava o comentário dela, e no
                      celular os dois viravam um bloco só.

                      O rótulo basta: o editor já tem contorno próprio, e cercar
                      um campo que já é uma caixa só empilharia moldura.
                    */}
                    <div>
                      <Typography.Text
                        type="secondary"
                        style={{
                          display: 'block',
                          fontSize: 11,
                          fontWeight: 600,
                          letterSpacing: 0.4,
                          textTransform: 'uppercase',
                          marginBottom: 4,
                        }}
                      >
                        {gabarito && !anulada
                          ? `Por que ${LETRAS[indice]!.toUpperCase()} está ${
                              LETRAS[indice] === gabarito ? 'certa' : 'errada'
                            }`
                          : `Comentário do item ${LETRAS[indice]!.toUpperCase()}`}
                      </Typography.Text>
                      <Form.Item name={[campo.name, 'commentary']} noStyle>
                        <EditorTexto minHeight={56} placeholder="Opcional" />
                      </Form.Item>
                    </div>
                  </Space>
                  <Button
                    type="text"
                    danger
                    aria-label={`Remover item ${LETRAS[indice]}`}
                    icon={<DeleteOutlined />}
                    onClick={() => remove(campo.name)}
                  />
                </Flex>
              ))}

              {campos.length < LETRAS.length ? (
                <Button type="dashed" block icon={<PlusOutlined />} onClick={() => add({})}>
                  Adicionar item {LETRAS[campos.length]}
                </Button>
              ) : null}
            </Space>
          )}
        </Form.List>

        <Divider />

        {/*
          O gabarito fica fora do Form.List de propósito: um Form.Item dentro da
          lista herda o caminho dela e seria salvo como `options.correctOption`,
          nunca chegando ao backend como gabarito.
        */}
        <Form.Item
          name="correctOption"
          label={anulada ? 'Gabarito preliminar' : 'Gabarito oficial'}
          extra={
            anulada
              ? 'A questão foi anulada: não há gabarito oficial. Esta letra fica guardada e nunca aparece para o aluno.'
              : undefined
          }
          style={{ marginBottom: 0 }}
        >
          <Select
            allowClear
            // Alvo de toque: 200px fixos sobram no desktop e ficam pequenos
            // demais para o polegar no celular.
            style={{ width: compacto ? '100%' : 200 }}
            placeholder="Escolha a alternativa"
            // Só as letras que existem: sem item "c", não há gabarito "c".
            options={LETRAS.slice(0, quantosItens).map((letra) => ({
              value: letra,
              label: letra.toUpperCase(),
            }))}
          />
        </Form.Item>
      </Card>

      <Card size="small" title="Comentário">
        <ComentarioResolucao name="commentary" />
      </Card>
    </>
  );
}

/**
 * Questão de prova discursiva.
 *
 * A prova discursiva vem dividida em dissertação, peça prática e questões
 * discursivas: cada parte dessas é uma questão aqui, com enunciado, espelho da
 * banca e resolução próprios. É por isso que esta tela não tem lista de itens -
 * quem tem vários itens é a prova, não a questão.
 */
function QuestaoDiscursiva() {
  const compacto = !Grid.useBreakpoint().md;

  /*
   * A legenda do cartão vira texto abaixo do título no celular.
   *
   * Como `extra`, ela disputa a linha do título: "Padrão oficial divulgado pela
   * banca" em 390px espremia "Espelho da banca" a ponto de quebrar as duas.
   */
  const legenda = (texto: string) =>
    compacto ? null : (
      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
        {texto}
      </Typography.Text>
    );

  const legendaAbaixo = (texto: string) =>
    compacto ? (
      <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 8 }}>
        {texto}
      </Typography.Paragraph>
    ) : null;

  return (
    <>
      <Card size="small" title="Tipo e enunciado">
        <Row gutter={16}>
          <Col xs={24} md={10}>
            <Form.Item
              name="essayKind"
              label="Parte da prova"
              rules={[{ required: true, message: 'Escolha a parte da prova.' }]}
            >
              <Select
                placeholder="Dissertação, peça prática..."
                options={TIPOS_DISCURSIVOS.map((tipo) => ({
                  value: tipo,
                  label: TIPO_DISCURSIVA_LABEL[tipo],
                }))}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={14}>
            <Form.Item
              name="label"
              label="Como aparece na prova"
              tooltip="O título exato usado pela banca, para o aluno reconhecer a questão."
            >
              <Input placeholder="I. Dissertação, Questão discursiva 1..." />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item name="statement" label="Enunciado" style={{ marginBottom: 0 }}>
          <EditorTexto minHeight={160} placeholder="Enunciado da questão" />
        </Form.Item>
      </Card>

      <Card
        size="small"
        title="Espelho da banca"
        // O aluno verá espelho e modelo separados, então vale deixar claro aqui
        // que este é o padrão oficial, não a nossa resposta.
        extra={legenda('Padrão oficial divulgado pela banca')}
      >
        {legendaAbaixo('Padrão oficial divulgado pela banca')}
        <Form.Item name="answerKey" noStyle>
          <EditorTexto minHeight={160} placeholder="Padrão de resposta divulgado pela banca" />
        </Form.Item>
      </Card>

      <Card
        size="small"
        title="Modelo de Resposta"
        extra={legenda('Resposta ideal escrita por especialista')}
      >
        {legendaAbaixo('Resposta ideal escrita por especialista')}
        <Form.Item name="answerModel" noStyle>
          <EditorTexto minHeight={160} placeholder="A resposta correta e completa, do jeito que o aluno deveria escrever" />
        </Form.Item>
      </Card>

      <Card size="small" title="Comentário">
        <ComentarioResolucao name="commentary" />
      </Card>
    </>
  );
}

/**
 * Como a questão se chama no backoffice.
 *
 * Nas discursivas, o que identifica é a parte da prova - "II. Peça prática" -,
 * não um número de ordem. Nas objetivas, o número é o que a banca usa.
 */
const tituloDaQuestao = nomeDaQuestao;

